import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "Data/Constants";

export class ProgressBar extends PIXI.Container {
    private readonly _bg: PIXI.Sprite;
    private _maxValue: number = 1;
    private _minValue: number = 0;
    private _curPerc: number = 0;
    private readonly _timeline: TimelineLite;
    private readonly _note: PIXI.Sprite;

    constructor() {
        super();

        this._timeline = new TimelineLite();

        this._bg = HLF.PixiUtils.makeSprite("musicstaff-base", Constants.SPRITESHEET_UI);

        this.addChild(this._bg);

        this._note = HLF.PixiUtils.makeSprite("musicstaff-note", Constants.SPRITESHEET_UI);
        this._note.position.x = this._bg.width / 2;
        this.addChild(this._note);

        const playhead = HLF.PixiUtils.makeSprite("musicstaff-playhead", Constants.SPRITESHEET_UI);
        playhead.pivot.x = 0;
        playhead.position.x = -this._bg.width / 2 + 15;
        this.addChild(playhead);

        this.alpha = 0;
        this.visible = false;
        this._curPerc = 0;
        this.updateProgress();
    }

    public get minValue(): number {
        return this._minValue;
    }

    public get maxValue(): number {
        return this._maxValue;
    }

    public get progressValue(): number {
        return HLF.MathUtils.lerp(this._minValue, this._maxValue, this._curPerc);
    }

    public set progressValue(pValue: number) {
        pValue = HLF.MathUtils.clamp(pValue, this._minValue, this._maxValue);
        // We subtract the min value to have values start at 0 and go to the max
        this._curPerc = (pValue - this._minValue) / (this._maxValue - this._minValue);
        this.updateProgress();
    }

    public get progressPerc(): number {
        return this._curPerc;
    }

    public set progressPerc(pValue: number) {
        pValue = HLF.MathUtils.clamp(pValue, 0, 1);
        this._curPerc = pValue;
        // console.log(this._curPerc);
        this.updateProgress();
    }

    public get isActive(): boolean {
        return this.visible;
    }

    public setMinMaxValues(pMinValue: number, pMaxValue: number): void {
        this._minValue = pMinValue;
        this._maxValue = pMaxValue;

        // Update progress
        this.updateProgress();
    }

    public show(pAnimate: boolean = true, pCallback?: () => void): void {
        if (pAnimate) {
            this._timeline.clear();
            this.scale.set(1, 1);
            this.visible = true;
            this._timeline.to(this, 0.25, {
                alpha: 1,
            });
            this._timeline.fromTo(this._note, 0.5, { alpha: 0 }, { alpha: 1 }, 0);

            if (pCallback !== undefined) {
                this._timeline.call(pCallback);
            }
        }
        else {
            this.visible = true;
            this.alpha = 1;
            if (pCallback !== undefined) {
                pCallback();
            }
        }
    }

    public hide(pAnimate: boolean = true, pCallback?: () => void): void {
        if (pAnimate) {
            this._timeline.clear();

            this._timeline.fromTo(this._note, 0.5, { alpha: 0 }, { alpha: 1 }, 0);
            this._timeline.to(this._note, 0.5, {
                pixi: {positionX: -this._bg.width / 2},
            }, 0);

            this._timeline.to(this, 0.5, {
                pixi: {
                    alpha: 0,
                },
            }, 0)
            .to(this, 0.25, {
                pixi: {
                    scaleX: 1.1,
                    scaleY: 1.1,
                },
            }, 0)
            .to(this, 0.25, {
                pixi: {
                    scaleX: 0.3,
                    scaleY: 0.3,
                },
            }, 0.25)
            .call(()=>{
                this.visible = false;
            });

            if (pCallback !== undefined) {
                this.visible = false;
                this._timeline.call(pCallback);
            }
        }
        else {
            this.alpha = 0;
            this.visible = false;
            if (pCallback !== undefined) {
                pCallback();
            }
        }
    }

    public destroy(pDestroyOptions?: {
        children?: boolean;
        texture?: boolean;
        baseTexture?: boolean;
    }) {
        this._timeline?.kill();
        super.destroy(pDestroyOptions);
    }

    private updateProgress(): void {
        this._curPerc = HLF.MathUtils.clamp(this._curPerc, 0, 1);

        const minX = -this._bg.width / 2 + this._note.width;
        const maxX = this._bg.width / 2;

        this._note.position.x = HLF.MathUtils.lerp(maxX, minX, this._curPerc);
    }
}
